
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface MediaUploadOptions {
  caption?: string;
  altText?: string;
  articleId?: string;
}

export const useMediaOperations = () => {
  const { toast } = useToast();

  const uploadMediaToStorage = async (
    file: File, 
    path: string = 'articles', // Default path if none provided
    options: MediaUploadOptions = {}
  ): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${path}/${fileName}`;

      // Determine media type
      const mediaType = file.type.startsWith('image/') 
        ? 'image' 
        : file.type.startsWith('video/') 
          ? 'video' 
          : 'document';

      console.log(`Uploading ${mediaType} to storage: ${filePath}`);

      // Upload file to storage
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('article-media')
        .upload(filePath, file);

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        throw uploadError;
      }

      console.log("File uploaded successfully:", uploadData);

      // Get public URL
      const { data } = supabase.storage
        .from('article-media')
        .getPublicUrl(filePath);

      const url = data.publicUrl;
      console.log("Public URL generated:", url);

      // Prepare metadata for database insert
      const mediaMetadata = {
        url,
        media_type: mediaType,
        caption: options.caption || null,
        alt_text: options.altText || null,
        article_id: options.articleId || null  // Make article_id nullable
      };
      
      console.log("Inserting media metadata into database:", mediaMetadata);
      
      // Insert metadata, making article_id nullable
      const { error: dbError, data: dbData } = await supabase
        .from('article_media')
        .insert([mediaMetadata]);

      if (dbError) {
        console.error('Media metadata could not be saved:', dbError);
        // Don't throw error here, we still want to return the URL
        // Just log warning that metadata wasn't saved
        console.warn('Media URL will be available but metadata was not saved to database');
      } else {
        console.log("Media metadata saved successfully:", dbData);
      }

      return url;
    } catch (error: any) {
      console.error('Error uploading media:', error);
      toast({
        variant: "destructive",
        title: "Error uploading media",
        description: error.message || "Failed to upload media"
      });
      return null;
    }
  };

  // Function to get all media
  const fetchMedia = async () => {
    try {
      const { data, error } = await supabase
        .from('article_media')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching media:', error);
      toast({
        variant: "destructive",
        title: "Error fetching media",
        description: error.message || "Failed to fetch media"
      });
      return [];
    }
  };

  // Function to update media metadata
  const updateMediaMetadata = async (
    id: string,
    metadata: { caption?: string; alt_text?: string; }
  ) => {
    try {
      const { error } = await supabase
        .from('article_media')
        .update(metadata)
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error: any) {
      console.error('Error updating media metadata:', error);
      toast({
        variant: "destructive",
        title: "Error updating media",
        description: error.message || "Failed to update media metadata"
      });
      return false;
    }
  };

  // Function to delete media
  const deleteMedia = async (id: string, url: string) => {
    try {
      // Extract the filepath from the URL
      const urlParts = url.split('/');
      const filePath = urlParts.slice(urlParts.indexOf('article-media') + 1).join('/');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('article-media')
        .remove([filePath]);

      if (storageError) {
        console.warn('Storage delete error:', storageError);
        // Continue anyway to clean up database
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('article_media')
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;
      
      return true;
    } catch (error: any) {
      console.error('Error deleting media:', error);
      toast({
        variant: "destructive",
        title: "Error deleting media",
        description: error.message || "Failed to delete media"
      });
      return false;
    }
  };

  return {
    uploadMediaToStorage,
    fetchMedia,
    updateMediaMetadata,
    deleteMedia
  };
};
